import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import OrderModal from "@/components/OrderModal";
import { Button } from "@/components/ui/button";
import { useCart } from "@/hooks/useCart";
import { photoUrl } from "@/utils/photoUrl";
import { useT } from "@/i18n/LanguageContext";
import { ArrowLeft, Minus, Plus, Trash2, ShoppingBag, ShoppingCart } from "lucide-react";

export default function Cart() {
  const navigate = useNavigate();
  const { items, removeItem, setQuantity, clear, totalQty, totalPrice } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const t = useT();

  return (
    <div className="min-h-screen bg-[#020409] text-white">
      <div className="border-b border-white/5 bg-black/60 backdrop-blur-md text-[11px] uppercase tracking-[0.2em] font-medium">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <button
            onClick={() => navigate("/products")}
            data-testid="cart-back-btn"
            className="flex items-center gap-2 text-zinc-500 hover:text-white transition"
          >
            <ArrowLeft className="w-3 h-3 text-[#39FF14]" /> {t("cart.back")}
          </button>
        </div>
      </div>

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#020409]/85 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="cart-logo">
            <Logo />
          </button>
          <button
            onClick={() => navigate("/")}
            data-testid="cart-home-btn"
            className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-[#39FF14] transition"
          >
            ← Home
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#39FF14] font-bold mb-2">{t("cart.eyebrow")}</p>
            <h1 className="font-headline text-4xl md:text-5xl font-black tracking-tighter">
              {totalQty === 0 ? t("cart.empty_title") : `${totalQty} ${totalQty === 1 ? t("cart.item") : t("cart.items")}`}
            </h1>
          </div>
          {items.length > 0 && (
            <Button
              onClick={() => clear()}
              data-testid="cart-clear-btn"
              className="bg-white/5 border border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 text-xs font-semibold uppercase tracking-wider"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {t("cart.clear")}
            </Button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="border border-white/5 bg-[#0A0F1A] rounded-2xl p-16 text-center" data-testid="cart-empty">
            <ShoppingBag className="w-14 h-14 text-zinc-700 mx-auto mb-5" />
            <h3 className="font-headline text-2xl font-bold text-white mb-2">{t("cart.empty_card_title")}</h3>
            <p className="text-zinc-500 text-sm mb-6">{t("cart.empty_card_body")}</p>
            <Button
              onClick={() => navigate("/products")}
              data-testid="cart-browse-btn"
              className="bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-wider text-xs shadow-[0_0_15px_rgba(57,255,20,0.4)]"
            >
              {t("cart.browse_catalog")}
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-[1fr_320px] gap-8">
            <div className="space-y-3" data-testid="cart-items-list">
              {items.map((it) => (
                <article
                  key={it.id}
                  data-testid="cart-row"
                  className="flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 bg-[#0A0F1A] border border-white/5 rounded-xl p-3"
                >
                  <img
                    src={photoUrl(it.photo)}
                    alt={it.name}
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-md object-contain bg-black border border-white/10 shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      onClick={() => navigate(`/product/${it.id}`)}
                      className="text-left font-semibold text-white text-sm hover:text-[#39FF14] transition truncate block w-full"
                    >
                      {it.name}
                    </button>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5 truncate">
                      {it.category} · {it.strength}
                    </p>
                    <p className="text-[#39FF14] font-headline font-black text-lg mt-1 tabular-nums">
                      €{(it.price * it.quantity).toFixed(2)}
                      <span className="block sm:inline text-[10px] text-zinc-500 font-normal uppercase tracking-wider sm:ml-1.5">
                        (€{it.price} × {it.quantity})
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2 w-full sm:w-auto justify-end shrink-0">
                    <button
                      type="button"
                      onClick={() => setQuantity(it.id, it.quantity - 1)}
                      data-testid="cart-qty-dec"
                      className="w-8 h-8 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-6 text-center font-bold text-white tabular-nums">{it.quantity}</span>
                    <button
                      type="button"
                      onClick={() => setQuantity(it.id, it.quantity + 1)}
                      data-testid="cart-qty-inc"
                      className="w-8 h-8 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeItem(it.id)}
                      data-testid="cart-remove-btn"
                      className="ml-1 sm:ml-2 w-8 h-8 rounded-md bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </article>
              ))}
            </div>

            <aside className="md:sticky md:top-24 bg-[#0A0F1A] border border-white/5 rounded-xl p-5 h-fit">
              <h3 className="font-headline text-lg font-bold mb-4 uppercase tracking-wider text-zinc-300">
                {t("cart.summary")}
              </h3>
              <div className="flex justify-between text-sm py-2 border-b border-white/5">
                <span className="text-zinc-400">{t("cart.summary_items")}</span>
                <span className="text-white font-semibold tabular-nums">{totalQty}</span>
              </div>
              <div className="flex justify-between text-sm py-2 border-b border-white/5">
                <span className="text-zinc-400">{t("cart.summary_subtotal")}</span>
                <span className="text-white font-semibold tabular-nums">€{totalPrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm py-2 mb-4">
                <span className="text-zinc-400">{t("cart.summary_shipping")}</span>
                <span className="text-zinc-500">{t("cart.summary_shipping_value")}</span>
              </div>
              <div className="flex items-baseline justify-between mb-5 pt-3 border-t border-[#39FF14]/20">
                <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 font-bold">{t("cart.summary_total")}</span>
                <span
                  className="font-headline font-black text-3xl text-[#39FF14] tabular-nums"
                  style={{ textShadow: "0 0 12px rgba(57,255,20,0.55)" }}
                  data-testid="cart-total"
                >
                  €{totalPrice.toFixed(2)}
                </span>
              </div>
              <Button
                onClick={() => setCheckoutOpen(true)}
                data-testid="cart-checkout-btn"
                className="w-full h-12 bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(57,255,20,0.4)] hover:shadow-[0_0_30px_rgba(57,255,20,0.6)]"
              >
                <ShoppingCart className="w-4 h-4 mr-2" /> {t("cart.place_order")}
              </Button>
              <p className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
                {t("cart.next_step_note")}
              </p>
            </aside>
          </div>
        )}
      </main>

      <OrderModal
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        items={items}
        onSuccess={() => clear()}
      />
    </div>
  );
}
